import React, { useEffect, useRef, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Users } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const HEARTBEAT_MS = 30_000;
const POLL_MS = 30_000;

const initials = (nameOrEmail) => {
  const s = (nameOrEmail || '').trim();
  if (!s) return '?';
  const parts = s.split(/[\s@.]+/).filter(Boolean);
  return (parts[0]?.[0] || '').toUpperCase() + (parts[1]?.[0] || '').toUpperCase();
};

const relativeTime = (iso) => {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - t);
  const s = Math.round(diff / 1000);
  if (s < 30) return 'just now';
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  return `${h}h ago`;
};

export default function ActiveUsersWidget() {
  const [users, setUsers] = useState([]);
  const [count, setCount] = useState(0);
  const mounted = useRef(true);

  const beat = async () => {
    try {
      await fetch(`${API_URL}/api/activity/heartbeat`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      /* network blip — ignore */
    }
  };

  const poll = async () => {
    try {
      const res = await fetch(`${API_URL}/api/activity/active`, { credentials: 'include' });
      if (!res.ok) return;
      const json = await res.json();
      if (!mounted.current) return;
      setUsers(json.users || []);
      setCount(json.count || 0);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    mounted.current = true;
    // Fire immediately so the widget is accurate within seconds of mount.
    beat().then(poll);

    const beatTimer = setInterval(beat, HEARTBEAT_MS);
    const pollTimer = setInterval(poll, POLL_MS);
    const onFocus = () => { beat(); poll(); };
    window.addEventListener('focus', onFocus);

    return () => {
      mounted.current = false;
      clearInterval(beatTimer);
      clearInterval(pollTimer);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  // Show 0 with a muted dot — never hide the widget entirely so users know it exists.
  const dotColor = count > 1 ? 'bg-emerald-500' : count === 1 ? 'bg-emerald-400' : 'bg-slate-300';
  const dotPulse = count > 0 ? 'animate-pulse' : '';

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="group inline-flex items-center gap-2 h-9 px-3 rounded-full border border-emerald-200/70 bg-emerald-50/60 hover:bg-emerald-50 hover:border-emerald-300 transition-colors text-emerald-700"
          data-testid="active-users-trigger"
          aria-label={`${count} ${count === 1 ? 'user' : 'users'} online`}
        >
          <span className="relative inline-flex h-2 w-2">
            <span className={`absolute inline-flex h-full w-full rounded-full opacity-60 ${dotColor} ${dotPulse}`} />
            <span className={`relative inline-flex h-2 w-2 rounded-full ${dotColor}`} />
          </span>
          <Users className="h-3.5 w-3.5 text-emerald-600" />
          <span className="text-[12px] font-semibold tabular-nums leading-none">
            {count}
            <span className="hidden sm:inline ml-1 text-[10.5px] font-medium text-emerald-700/80 uppercase tracking-wider">
              online
            </span>
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={8} className="w-72 p-0 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-emerald-50 via-white to-emerald-50/50">
          <div className="flex items-center gap-2">
            <span className="relative inline-flex h-2 w-2">
              <span className={`absolute inline-flex h-full w-full rounded-full opacity-60 ${dotColor} ${dotPulse}`} />
              <span className={`relative inline-flex h-2 w-2 rounded-full ${dotColor}`} />
            </span>
            <h3 className="text-sm font-semibold text-slate-900">
              {count} {count === 1 ? 'user' : 'users'} online
            </h3>
          </div>
          <p className="text-[11px] text-slate-500 mt-0.5">Active within the last 5 minutes</p>
        </div>
        <div className="max-h-72 overflow-y-auto py-1.5">
          {users.length === 0 && (
            <div className="px-4 py-6 text-center text-[12.5px] text-slate-400">
              No active users right now.
            </div>
          )}
          {users.map((u) => (
            <div
              key={u.user_id || u.email}
              className="flex items-center gap-3 px-4 py-2 hover:bg-slate-50/60"
              data-testid={`active-user-${u.email || u.user_id}`}
            >
              <div className="relative shrink-0">
                {u.picture ? (
                  <img src={u.picture} alt="" className="w-8 h-8 rounded-full object-cover border border-slate-200" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white text-[11px] font-bold flex items-center justify-center">
                    {initials(u.name || u.email)}
                  </div>
                )}
                <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-500 border-2 border-white" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-semibold text-slate-800 truncate">
                  {u.name || (u.email || '').split('@')[0]}
                </div>
                <div className="text-[11px] text-slate-500 truncate">{u.email}</div>
              </div>
              <div className="text-[10px] text-slate-400 shrink-0">{relativeTime(u.last_seen)}</div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
